import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CheckCircle2, Copy, Plus, RefreshCw, RotateCcw, Trash2, X } from "lucide-react";
import AdminSideBar from "./AdminSideBar";
import { authFetch } from "../utils/auth";
import UserProfileMenu from "./UserProfileMenu";

const STATUS_STYLE = {
  active: "bg-green-100 text-green-700",
  expiring: "bg-orange-100 text-orange-700",
  disabled: "bg-gray-100 text-gray-500",
  expired: "bg-red-100 text-red-500",
};

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    + " " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};

const AdminApiKeysPage = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [keys, setKeys] = useState([]);
  const [users, setUsers] = useState([]);
  const [usernameMap, setUsernameMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState({});
  const [rotateTarget, setRotateTarget] = useState(null);
  const [newlyRotatedKey, setNewlyRotatedKey] = useState(null);
  const [modalCopied, setModalCopied] = useState(false);
  // Create key modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createKeyName, setCreateKeyName] = useState("");
  const [createOwner, setCreateOwner] = useState("");
  const [createLoading, setCreateLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Fetch all keys and all users in parallel
      const [keysRes, usersRes] = await Promise.all([
        authFetch("/api/admin/all-api-keys"),
        authFetch("/api/admin/users"),
      ]);

      if (!keysRes.ok) {
        const b = await keysRes.json().catch(() => ({}));
        throw new Error(b.detail || "Failed to fetch API keys.");
      }
      if (!usersRes.ok) {
        const b = await usersRes.json().catch(() => ({}));
        throw new Error(b.detail || "Failed to fetch users.");
      }

      const keysData = await keysRes.json();
      const usersData = await usersRes.json();

      // Build user_id → username map from cognito users
      const userList = usersData.users || [];
      setUsers(userList);
      const map = {};
      for (const u of userList) {
        if (u.user_id) map[u.user_id] = u.username;
        // Also map sub (cognito sub) if present
        if (u.sub) map[u.sub] = u.username;
      }
      setUsernameMap(map);
      const STATUS_ORDER = { active: 0, expiring: 1, expired: 2, disabled: 3 };
      const sorted = (keysData.keys || []).slice().sort((a, b) => {
        const sa = STATUS_ORDER[a.status] ?? 99;
        const sb = STATUS_ORDER[b.status] ?? 99;
        return sa - sb;
      });
      setKeys(sorted);
    } catch (err) {
      setError(err.message || "Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resolveOwner = (owner_user_id) => {
    if (!owner_user_id) return "—";
    return usernameMap[owner_user_id] || owner_user_id;
  };

  const effectiveStatus = (item) => {
    if (!item.expires_at) return item.status || "active";
    const isPast = new Date(item.expires_at) < new Date();
    if (isPast && item.status !== "disabled") return "expired";
    return item.status || "active";
  };

  const handleDisable = async (keyId) => {
    setActionLoading((p) => ({ ...p, [keyId]: "disable" }));
    setError("");
    try {
      const res = await authFetch(`/api/admin/api-keys/${keyId}/disable`, { method: "POST" });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.detail || "Failed to disable key.");
      }
      await fetchData();
    } catch (err) {
      setError(err.message || "Failed to disable key.");
    } finally {
      setActionLoading((p) => ({ ...p, [keyId]: null }));
    }
  };

  const handleRotate = async () => {
    const keyId = rotateTarget?.key_id;
    const keyName = rotateTarget?.key_name || "Rotated Key";
    if (!keyId) return;
    setRotateTarget(null);
    setActionLoading((p) => ({ ...p, [keyId]: "rotate" }));
    setError("");
    try {
      const res = await authFetch(`/api/admin/api-keys/${keyId}/rotate`, { method: "POST" });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.detail || "Failed to rotate key.");
      }
      const payload = await res.json().catch(() => ({}));
      if (payload?.api_key) {
        setNewlyRotatedKey({ name: keyName, api_key: String(payload.api_key), key_id: payload.key_id || "" });
      }
      await fetchData();
    } catch (err) {
      setError(err.message || "Failed to rotate key.");
    } finally {
      setActionLoading((p) => ({ ...p, [keyId]: null }));
    }
  };

  const handleCreateKey = async () => {
    if (!createKeyName.trim()) return;
    setCreateLoading(true);
    setError("");
    try {
      const res = await authFetch("/api/admin/api-keys", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: createKeyName.trim(),
          owner_user_id: createOwner || null,
          requests_per_minute: 120,
        }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.detail || "Failed to create API key.");
      }
      const payload = await res.json().catch(() => ({}));
      setShowCreateModal(false);
      setCreateKeyName("");
      setCreateOwner("");
      if (payload?.api_key) {
        setNewlyRotatedKey({ name: createKeyName.trim(), api_key: String(payload.api_key), key_id: payload.key_id || "" });
      }
      await fetchData();
    } catch (err) {
      setError(err.message || "Failed to create API key.");
    } finally {
      setCreateLoading(false);
    }
  };

  const copyNewKey = async () => {
    if (!newlyRotatedKey?.api_key) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(newlyRotatedKey.api_key);
      } else {
        const el = document.createElement("textarea");
        el.value = newlyRotatedKey.api_key;
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      setModalCopied(true);
      setTimeout(() => setModalCopied(false), 2000);
    } catch {
      setError("Unable to copy. Please copy manually.");
    }
  };

  const visibleKeys = keys.filter((item) => {
    const st = effectiveStatus(item);
    return st !== "disabled" && st !== "expired";
  });

  return (
    <div className="flex h-screen w-full bg-[#FAFAF6] overflow-hidden">
      <AdminSideBar onLogout={onLogout} />

      {/* Main */}
      <div className="flex-1 relative flex flex-col px-6 sm:px-10 lg:px-16 xl:px-24 py-8 overflow-auto">
        <div className="absolute top-4 right-6 z-50">
          <UserProfileMenu onLogout={onLogout} />
        </div>
        <div className="w-full">
          <div className="flex items-center justify-between mb-6 pr-14">
            <div>
              <h1 className="text-2xl font-bold text-[#253439]">API Key Management</h1>
              <p className="text-gray-500 text-sm mt-1">All API keys across all users.</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const agents = users.filter((u) => (u.roles || []).some((r) => r === "support_agent" || r === "service"));
                  setCreateKeyName("");
                  setCreateOwner(agents[0]?.user_id || "");
                  setShowCreateModal(true);
                }}
                className="flex items-center gap-2 bg-[#253439] hover:bg-[#1a2528] text-white px-4 py-2 rounded-lg text-sm font-semibold transition"
              >
                <Plus size={16} />
                Create Key
              </button>
              <button
                onClick={fetchData}
                disabled={loading}
                className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition disabled:opacity-50"
              >
                <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
                Refresh
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
          )}

          {loading ? (
            <div className="text-center py-16 text-gray-400">Loading…</div>
          ) : visibleKeys.length === 0 ? (
            <div className="text-center py-16 text-gray-400">No API keys found.</div>
          ) : (
            <div className="border border-[#EBECE6] rounded-xl overflow-x-auto bg-white shadow-sm">
              <table className="w-full text-sm min-w-[1050px]">
                <thead>
                  <tr className="bg-[#f6f6f1] text-left text-gray-600">
                    <th className="px-4 py-3 font-semibold">Key Name</th>
                    <th className="px-4 py-3 font-semibold">User</th>
                    <th className="px-4 py-3 font-semibold">Created By</th>
                    <th className="px-4 py-3 font-semibold">Key ID</th>
                    <th className="px-4 py-3 font-semibold">Created At</th>
                    <th className="px-4 py-3 font-semibold">Expires At</th>
                    <th className="px-4 py-3 font-semibold">Last Used</th>
                    <th className="px-4 py-3 font-semibold text-right">Requests</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleKeys.map((item) => {
                    const st = effectiveStatus(item);
                    const isDisabled = st === "disabled" || st === "expired";
                    const canRotate = st === "active";
                    const busy = actionLoading[item.key_id];
                    return (
                      <tr key={item.key_id} className={`border-t border-[#EBECE6] transition ${isDisabled ? "opacity-60" : "hover:bg-[#fafaf8]"}`}>
                        <td className="px-4 py-3 font-medium text-[#253439]">{item.key_name || "—"}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-gray-700 font-medium">{resolveOwner(item.owner_user_id)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {item.rotated_by_user_id
                            ? <span title={`Rotated by admin: ${resolveOwner(item.rotated_by_user_id)}`}>{resolveOwner(item.rotated_by_user_id)} <span className="text-xs text-cyan-600 font-semibold">(admin)</span></span>
                            : item.created_by_user_id
                              ? <span title={`Created by admin: ${resolveOwner(item.created_by_user_id)}`}>{resolveOwner(item.created_by_user_id)} <span className="text-xs text-cyan-600 font-semibold">(admin)</span></span>
                              : resolveOwner(item.owner_user_id)
                          }
                        </td>
                        <td className="px-4 py-3">
                          <code className="text-xs text-gray-500 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded font-mono">
                            {item.key_id || "—"}
                          </code>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600">{formatDate(item.created_at)}</td>
                        <td className="px-4 py-3 text-xs text-gray-600">{formatDate(item.expires_at)}</td>
                        <td className="px-4 py-3 text-xs text-gray-600">{formatDate(item.last_used_at)}</td>
                        <td className="px-4 py-3 text-right text-gray-700">{Number(item.total_requests || 0)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLE[st] || "bg-gray-100 text-gray-500"}`}>
                            {st.charAt(0).toUpperCase() + st.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {canRotate && (
                              <button
                                type="button"
                                disabled={!!busy}
                                onClick={() => setRotateTarget(item)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md border border-cyan-200 text-cyan-700 hover:bg-cyan-50 disabled:opacity-50"
                                title="Rotate key"
                              >
                                {busy === "rotate" ? <RefreshCw size={13} className="animate-spin" /> : <RotateCcw size={13} />}
                                Rotate
                              </button>
                            )}
                            {!isDisabled && (
                              <button
                                type="button"
                                disabled={!!busy}
                                onClick={() => handleDisable(item.key_id)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                                title="Delete key"
                              >
                                {busy === "disable" ? <RefreshCw size={13} className="animate-spin" /> : <Trash2 size={13} />}
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {/* Create Key modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-[220] bg-black/40 flex items-center justify-center px-4">
          <div className="w-full max-w-md bg-white border border-gray-200 rounded-2xl shadow-xl">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#1f1f1f]">Create API Key</h2>
              <button type="button" onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Key Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={createKeyName}
                  onChange={(e) => setCreateKeyName(e.target.value)}
                  placeholder="Enter Key Name"
                  maxLength={128}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#253439] focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Assign to User</label>
                <select
                  value={createOwner}
                  onChange={(e) => setCreateOwner(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#253439] focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 outline-none bg-white"
                >
                  {users
                    .filter((u) => (u.roles || []).some((r) => r === "support_agent" || r === "service"))
                    .map((u) => (
                      <option key={u.user_id || u.username} value={u.user_id || ""}>
                        {u.username}{u.user_id ? " (" + u.user_id.slice(0, 8) + "\u2026)" : ""}
                      </option>
                    ))
                  }
                </select>
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowCreateModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
                <button
                  type="button"
                  onClick={handleCreateKey}
                  disabled={createLoading || !createKeyName.trim()}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#253439] text-white text-sm hover:bg-[#1a2528] disabled:opacity-50"
                >
                  {createLoading ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={14} />}
                  Create Key
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rotate confirm modal */}
      {rotateTarget && (
        <div className="fixed inset-0 z-[220] bg-black/40 flex items-center justify-center px-4">
          <div className="w-full max-w-md bg-white border border-gray-200 rounded-2xl shadow-xl">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#1f1f1f]">Rotate API Key?</h2>
              <button type="button" onClick={() => setRotateTarget(null)} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-700">
                This will generate a <strong>new secret key</strong> for <span className="font-mono text-xs bg-gray-100 px-1 rounded">{rotateTarget.key_name}</span> (owned by <strong>{resolveOwner(rotateTarget.owner_user_id)}</strong>). The old key stays active for <strong>10 minutes</strong>.
              </p>
              <p className="text-sm text-orange-600 font-medium">⚠ The new key will be shown once. Make sure the key owner updates their integration.</p>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setRotateTarget(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="button" onClick={handleRotate} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-600 text-white text-sm hover:bg-cyan-700">
                  <RotateCcw size={14} /> Rotate Key
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New key reveal modal */}
      {newlyRotatedKey && (
        <div className="fixed inset-0 z-[230] bg-black/50 flex items-center justify-center px-4">
          <div className="w-full max-w-lg bg-white border border-gray-200 rounded-2xl shadow-xl">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#1f1f1f]">New key generated</h2>
              <button type="button" onClick={() => { setNewlyRotatedKey(null); setModalCopied(false); }} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                <span className="text-amber-500 text-lg leading-none mt-0.5">⚠</span>
                <p className="text-sm text-amber-800 font-medium">
                  Share this key with the key owner. <strong>It will not be shown again</strong> after you close this window.
                </p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Secret key — {newlyRotatedKey.name}</label>
                {newlyRotatedKey.key_id && (
                  <p className="text-xs text-gray-400 mb-2 font-mono">Key ID: {newlyRotatedKey.key_id}</p>
                )}
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm text-gray-600 bg-gray-50 border border-gray-200 px-3 py-2.5 rounded-lg font-mono">
                    {"•".repeat(24)}...{newlyRotatedKey.api_key.slice(-4)}
                  </code>
                  <button
                    type="button"
                    onClick={copyNewKey}
                    className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium border transition ${modalCopied ? "bg-green-50 border-green-300 text-green-700" : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"}`}
                  >
                    {modalCopied ? <CheckCircle2 size={15} /> : <Copy size={15} />}
                    {modalCopied ? "Copied!" : "Copy"}
                  </button>
                </div>
              </div>
              <div className="pt-2 flex justify-end">
                <button type="button" onClick={() => { setNewlyRotatedKey(null); setModalCopied(false); }} className="px-5 py-2 rounded-lg bg-[#111111] text-white text-sm font-medium hover:bg-black">
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminApiKeysPage;
